/**
 * Is now a safe moment to load a waiting new build? (See utils/appUpdate.ts.)
 *
 * Loading it reloads the page, so it must not happen while the user is in the
 * middle of something the reload would throw away:
 * - a dialog is open (an edit in progress, a confirmation, a picker);
 * - a screen holds unsaved work (useUnsavedWork, or `data-dirty="true"`);
 * - they are typing (a key pressed in the last TYPING_QUIET_MS in a text field
 *   that still has the focus).
 *
 * Two moments are considered:
 * - 'navigation': the user moved to another section. The page they left is
 *   gone, along with anything in it, so the checks above are enough.
 * - 'return': the user came back to a tab that was hidden for at least
 *   RETURN_AFTER_HIDDEN_MS. The page they left is still there and may hold
 *   input that no dirty flag knows about (a half-filled form, a typed filter),
 *   so it also must not have had any field edited since it was opened, and
 *   sections that keep work in progress across their own screens (Selective
 *   Touch, the kiosk screens, which apply updates when idle instead) never
 *   reload on return.
 */
import { hasUnsavedWork } from './unsavedWork';

export type UpdateMoment = 'navigation' | 'return';

/** A key pressed in a text field this recently means the user is typing. */
export const TYPING_QUIET_MS = 5_000;
/** A tab must have been hidden at least this long for coming back to it to count. */
export const RETURN_AFTER_HIDDEN_MS = 5 * 60 * 1000;
/** Sections that are never reloaded on a return to the tab (first path segment). */
export const NO_RELOAD_ON_RETURN = new Set(['selective-touch', 'tablet', 'bedside', 'self-order']);

/**
 * Open dialogs: MUI (and its exit transition), DevExtreme popups, native <dialog>,
 * and anything else that declares itself a dialog.
 */
const DIALOG_SELECTOR = '.MuiDialog-root, [role="dialog"], [role="alertdialog"], [aria-modal="true"], dialog[open]';

let lastTypedAt = 0;
let lastTypedIn: Element | null = null;
let editedSincePageChange = false;
let trackingInstalled = false;

function isTextEntry(target: EventTarget | null): boolean {
  if (!target || typeof (target as Element).tagName !== 'string') return false;
  const el = target as HTMLElement;
  if (el.isContentEditable) return true;
  if (el.tagName === 'TEXTAREA') return true;
  if (el.tagName !== 'INPUT') return false;
  const type = (el as HTMLInputElement).type;
  return !['button', 'checkbox', 'radio', 'submit', 'reset', 'file', 'image', 'color', 'range'].includes(type);
}

/** Starts noticing typing and field edits. Once per page; safe to call again. */
export function installUpdateMomentTracking(doc: Document = document): void {
  if (trackingInstalled) return;
  trackingInstalled = true;
  // Capture phase: a component that stops propagation still counts.
  doc.addEventListener('keydown', (e) => {
    if (!isTextEntry(e.target)) return;
    lastTypedAt = Date.now();
    lastTypedIn = e.target as Element;
  }, true);
  const edited = () => { editedSincePageChange = true; };
  doc.addEventListener('input', edited, true);
  doc.addEventListener('change', edited, true);
}

/** The user opened another page: edits on the one before no longer count. */
export function notePageChange(): void {
  editedSincePageChange = false;
}

function isShown(el: Element): boolean {
  const check = (el as Element & { checkVisibility?: (opts?: object) => boolean }).checkVisibility;
  if (typeof check === 'function') return check.call(el, { visibilityProperty: true });
  // No layout to ask (jsdom): trust the element unless it is hidden outright.
  if ((el as HTMLElement).hidden) return false;
  const view = el.ownerDocument.defaultView;
  if (!view) return true;
  const style = view.getComputedStyle(el);
  return style.display !== 'none' && style.visibility !== 'hidden';
}

/** An open dialog, or null. */
export function openDialog(doc: Document = document): Element | null {
  for (const el of Array.from(doc.querySelectorAll(DIALOG_SELECTOR))) {
    if (isShown(el)) return el;
  }
  return null;
}

/** First path segment, lower case ('' for the root). */
function sectionOf(pathname: string): string {
  return pathname.split('/').filter(Boolean)[0]?.toLowerCase() ?? '';
}

/**
 * Why now is NOT a safe moment to reload into the new build, or null when it is.
 */
export function updateBlocker(
  moment: UpdateMoment,
  opts: { doc?: Document; now?: number; pathname?: string } = {},
): 'dialog' | 'unsaved' | 'typing' | 'section' | 'edited' | null {
  const doc = opts.doc ?? document;
  const now = opts.now ?? Date.now();
  if (openDialog(doc)) return 'dialog';
  if (hasUnsavedWork(doc)) return 'unsaved';
  if (now - lastTypedAt < TYPING_QUIET_MS && lastTypedIn?.isConnected && doc.activeElement === lastTypedIn) return 'typing';
  if (moment === 'return') {
    const pathname = opts.pathname ?? doc.defaultView?.location.pathname ?? '';
    if (NO_RELOAD_ON_RETURN.has(sectionOf(pathname))) return 'section';
    if (editedSincePageChange) return 'edited';
  }
  return null;
}

/** Tests only. */
export function resetUpdateMomentForTests(state: { lastTypedAt?: number; edited?: boolean } = {}): void {
  lastTypedAt = state.lastTypedAt ?? 0;
  lastTypedIn = null;
  editedSincePageChange = state.edited ?? false;
}
